import logger from 'js/app/loggerSingleton';

import type { BrazeInAppMessage, ClickBrazeInAppMessage, ViewBrazeInAppMessage } from '@coursera/event-pulse-types';
import { track } from '@coursera/event-pulse/sdk';

const SHOWN_EVENT = 'coursera:braze:iam_shown';
const CLICKED_EVENT = 'coursera:braze:iam_clicked';

/**
 * CustomEvent detail payload received from the Braze bridge
 */
type BrazeIamDetail = {
  campaignId: string;
  campaignName?: string;
  messageVariationId?: string;
  messageType?: 'slideup' | 'modal' | 'fullscreen' | 'html';
  uri?: string;
  buttonId?: string;
  clickType?: 'cta_button' | 'image_click' | 'close_button' | 'background_dismiss' | 'no_cta';
};

/**
 * Payload data for view_braze_in_app_message event (without page/user_properties)
 * The track() SDK automatically adds page and user_properties
 */
type ViewBrazeInAppMessageData = Pick<ViewBrazeInAppMessage, 'brazeInAppMessage'>;

/**
 * Payload data for click_braze_in_app_message event (without page/user_properties)
 * The track() SDK automatically adds page and user_properties
 */
type ClickBrazeInAppMessageData = Pick<ClickBrazeInAppMessage, 'brazeInAppMessage' | 'buttonId' | 'clickType'>;

declare global {
  interface Window {
    __courseraBrazeBridge__?: boolean;
  }
}

const isValidDetail = (value: unknown): value is BrazeIamDetail => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const detail = value as Record<string, unknown>;

  // campaignId is required
  if (typeof detail.campaignId !== 'string') {
    return false;
  }

  // Validate optional enum fields if present
  if (
    detail.messageType !== undefined &&
    !['slideup', 'modal', 'fullscreen', 'html'].includes(detail.messageType as string)
  ) {
    return false;
  }

  if (
    detail.clickType !== undefined &&
    !['cta_button', 'image_click', 'close_button', 'background_dismiss', 'no_cta'].includes(detail.clickType as string)
  ) {
    return false;
  }

  return true;
};

/**
 * Send Braze event using the event-pulse SDK with proper typing.
 * Event names from @coursera/event-pulse-types:
 * - view_braze_in_app_message: ViewBrazeInAppMessage type
 * - click_braze_in_app_message: ClickBrazeInAppMessage type
 */
function trackBrazeEvent(
  eventName: 'view_braze_in_app_message' | 'click_braze_in_app_message',
  data: ViewBrazeInAppMessageData | ClickBrazeInAppMessageData
): Promise<void> {
  return track(eventName, data).catch(logger.warn);
}

const forward = (
  eventName: typeof SHOWN_EVENT | typeof CLICKED_EVENT,
  trackedName: 'view_braze_in_app_message' | 'click_braze_in_app_message'
): EventListener => {
  return (event: Event) => {
    const detail = (event as CustomEvent<unknown>).detail;
    if (!isValidDetail(detail)) {
      logger.warn(`Braze bridge: invalid detail on ${eventName}`, detail);
      return;
    }

    const { campaignId, campaignName, messageVariationId, messageType, uri, buttonId, clickType } = detail;

    const brazeInAppMessage: BrazeInAppMessage = {
      campaignId,
      ...(campaignName && { campaignName }),
      ...(messageVariationId && { messageVariationId }),
      ...(messageType && { messageType }),
      ...(uri && { uri }),
    };

    const basePayload: ViewBrazeInAppMessageData = {
      brazeInAppMessage,
    };

    let payload: ViewBrazeInAppMessageData | ClickBrazeInAppMessageData;

    if (trackedName === 'click_braze_in_app_message') {
      payload = {
        ...basePayload,
        ...(buttonId && { buttonId }),
        ...(clickType && { clickType }),
      } as ClickBrazeInAppMessageData;
    } else {
      payload = basePayload;
    }

    trackBrazeEvent(trackedName, payload);
  };
};

const setupBrazeEventBridge = (): void => {
  if (typeof window === 'undefined' || window.__courseraBrazeBridge__) {
    return;
  }
  window.__courseraBrazeBridge__ = true;

  window.addEventListener(SHOWN_EVENT, forward(SHOWN_EVENT, 'view_braze_in_app_message'));
  window.addEventListener(CLICKED_EVENT, forward(CLICKED_EVENT, 'click_braze_in_app_message'));
};

export default setupBrazeEventBridge;
